import { typeof as _typeof, objectSpread2 as _objectSpread2, defineProperty as _defineProperty, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useSafeThemeUI, useForkRef, utils, useSelectState, useDrawerContext, useBoolean, useTimeout, useSize, scrollbarWidth, useIsomorphicEffect, Text } from '@costar/theme-ui';
import React, { forwardRef, useRef, useMemo, useState, useCallback, useEffect } from 'react';
import multiSelectTextResolver from './multi-select-text-resolver.esm.js';
import defaultRenderItem from './render-default-item.esm.js';
import defaultRenderPopoverContent from './render-popover-content.esm.js';
import renderTabOptionsContent from './render-tab-options-content.esm.js';
import singleSelectTextResolver from './single-select-text-resolver.esm.js';
import useExtractProps from './use-extract-props.esm.js';
import { getOptionKey } from './utils.esm.js';
import PopoverSelectContainer from '../popover-select-container/popover-select-container.esm.js';

function flattenSelectObject(options, groupKey) {
  return Object.keys(options).reduce(function (acc, cur) {
    var nextOption = options[cur];
    var nextOptions = _typeof(nextOption) === 'object' ? nextOption.options : {};
    return utils.isNotEmptyObject(nextOptions) ? _objectSpread2(_objectSpread2({}, acc), flattenSelectObject(nextOptions, getOptionKey(cur, groupKey))) : _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, getOptionKey(cur, groupKey), nextOption));
  }, {});
}
var defaultListProps = {
  disablePadding: true
};
var defaultIconProps = {
  color: 'neutral.darker'
};
var Select = /*#__PURE__*/forwardRef(function Select(props, ref) {
  var _useDrawerContext;
  var _useExtractProps = useExtractProps(props),
    type = _useExtractProps.type,
    size = _useExtractProps.size,
    className = _useExtractProps.className,
    _listProps = _useExtractProps._listProps,
    options = _useExtractProps.options,
    resolveText = _useExtractProps.resolveText,
    showSelectionCheckbox = _useExtractProps.showSelectionCheckbox,
    isMultiSelect = _useExtractProps.isMultiSelect,
    onChange = _useExtractProps.onChange,
    initialSelection = _useExtractProps.initialSelection,
    renderItem = _useExtractProps.renderItem,
    renderPopoverContextProps = _useExtractProps.renderPopoverContextProps,
    extendedContent = _useExtractProps.extendedContent,
    extendedContentTop = _useExtractProps.extendedContentTop,
    placeHolder = _useExtractProps.placeHolder,
    popoverProps = _useExtractProps.popoverProps,
    fullWidthPopover = _useExtractProps.fullWidthPopover,
    containerElement = _useExtractProps.containerElement,
    selectionProps = _useExtractProps.selectionProps,
    paneProps = _useExtractProps.paneProps,
    placeHolderProps = _useExtractProps.placeHolderProps,
    iconProps = _useExtractProps.iconProps,
    automationId = _useExtractProps.automationId,
    renderSelection = _useExtractProps.renderSelection,
    onClose = _useExtractProps.onClose,
    rest = _useExtractProps.rest;

  // if (process.env.NODE_ENV !== 'production') {
  //   // eslint-disable-next-line no-console
  //   console.warn(
  //     '[<Select /> is a deprecated] --- please switch to AutoComplete instead'
  //   )
  // }

  var theme = useSafeThemeUI(props);
  var _ref = theme,
    transition = _ref.scrollbar.transition;
  var buttonRef = useRef(null);
  var handelRef = useForkRef(ref, buttonRef);
  var flattenSelectionOptions = useMemo(function () {
    return Array.isArray(options) ? {} : flattenSelectObject(options);
  }, [options]);
  var _useSelectState = useSelectState(selectionProps, initialSelection, onChange, isMultiSelect, type === 'tabular', Object.keys(flattenSelectionOptions)),
    selection = _useSelectState.value,
    selectionClick = _useSelectState.handler,
    allSelected = _useSelectState.allSelected;
  var _ref2 = (_useDrawerContext = useDrawerContext()) !== null && _useDrawerContext !== void 0 ? _useDrawerContext : {},
    onEnteredObserver = _ref2.onEnteredObserver;
  var paneRef = useRef(null);
  var listRef = useRef(null);
  var _useBoolean = useBoolean(),
    _useBoolean2 = _slicedToArray(_useBoolean, 2),
    toggleValue = _useBoolean2[0],
    toggleAction = _useBoolean2[1];
  var delayExecute = useTimeout(150);
  var _useState = useState(defaultListProps),
    _useState2 = _slicedToArray(_useState, 2),
    listProps = _useState2[0],
    setListProps = _useState2[1];
  var renderPopoverContent = useMemo(function () {
    return renderPopoverContextProps ? renderPopoverContextProps : (type === 'tabular' ? renderTabOptionsContent : defaultRenderPopoverContent)(renderItem ? renderItem : defaultRenderItem(showSelectionCheckbox, isMultiSelect, allSelected), showSelectionCheckbox, listProps, isMultiSelect, extendedContent, extendedContentTop);
  }, [type, renderPopoverContextProps, listProps, renderItem, showSelectionCheckbox, isMultiSelect, allSelected, extendedContent, extendedContentTop]);
  var containerSize = useSize(containerElement);
  function getScrollBarWidth() {
    return paneRef.current && listRef.current && (listRef.current.getBoundingClientRect().height > paneRef.current.getBoundingClientRect().height || paneRef.current.scrollHeight > paneRef.current.clientHeight) ? scrollbarWidth // we are using theme-ui webkit definition scroll bar
    : 0;
  }
  var updateListWidth = useCallback(function (prevState) {
    if (buttonRef.current) {
      var refRect = buttonRef.current.getBoundingClientRect();
      var newWidth = refRect.width - 2 - getScrollBarWidth();
      return _objectSpread2(_objectSpread2({}, prevState), {}, {
        style: _objectSpread2(_objectSpread2(_objectSpread2({}, prevState.style), fullWidthPopover ? {
          minWidth: newWidth
        } : {}), containerSize !== null && containerSize !== void 0 && containerSize.height ? {
          maxHeight: containerSize.height - (refRect.top + refRect.height)
        } : {}),
        ref: listRef
      });
    }
    return prevState;
  }, [containerSize, fullWidthPopover]);
  var handlePopupMounted = useCallback(function () {
    setListProps(function (state) {
      return updateListWidth(state);
    });
  }, [updateListWidth]);
  useEffect(function () {
    setListProps(function (state) {
      return _objectSpread2(_objectSpread2({}, state), _listProps);
    });
  }, [_listProps]);
  useIsomorphicEffect(function () {
    if (buttonRef.current && listRef.current) {
      var option = listRef.current.querySelector(".is-selected");
      if (option) {
        var element = option;
        element.scrollIntoView();
      }
    }
  });
  useEffect(function () {
    setListProps(function (state) {
      return updateListWidth(state);
    });
  }, [updateListWidth]);
  useEffect(function () {
    var handelOnEntered = function handelOnEntered() {
      delayExecute(function () {
        return setListProps(function (state) {
          return updateListWidth(state);
        });
      });
    };
    onEnteredObserver === null || onEnteredObserver === void 0 ? void 0 : onEnteredObserver.attach(handelOnEntered);
    return function () {
      return onEnteredObserver === null || onEnteredObserver === void 0 ? void 0 : onEnteredObserver.detach(handelOnEntered);
    };
  }, [delayExecute, onEnteredObserver, updateListWidth]);
  var singleSelectionTextResolver = singleSelectTextResolver(resolveText, flattenSelectionOptions);
  var multiSelectionTextResolver = multiSelectTextResolver(resolveText, flattenSelectionOptions);
  var onToggleClose = useCallback(function () {
    if (onClose) {
      onClose();
    }
    toggleAction.off();
  }, [onClose, toggleAction]);
  var additionalProps = useMemo(function () {
    return _objectSpread2({
      popoverProps: _objectSpread2({
        children: renderPopoverContent(options, selection, onToggleClose, selectionClick)
      }, popoverProps),
      paneProps: _objectSpread2({
        ref: paneRef,
        onMounted: handlePopupMounted,
        sx: _objectSpread2({}, transition)
      }, paneProps),
      icon: _objectSpread2(_objectSpread2({}, defaultIconProps), iconProps)
    }, rest);
  }, [handlePopupMounted, iconProps, options, paneProps, popoverProps, renderPopoverContent, rest, selection, selectionClick, onToggleClose, transition]);
  return /*#__PURE__*/React.createElement(PopoverSelectContainer, _extends({
    "automation-id": automationId,
    ref: handelRef,
    className: className,
    size: size,
    onOpen: toggleAction.on,
    onClose: onToggleClose,
    open: toggleValue
  }, additionalProps), selection !== undefined && selection !== null && !Array.isArray(selection) ? renderSelection ? renderSelection(selection) : singleSelectionTextResolver(selection) : Array.isArray(selection) && selection.length > 0 ? renderSelection ? renderSelection(selection) : multiSelectionTextResolver(selection) : /*#__PURE__*/React.createElement(Text, _extends({
    className: "csg-ui-select-placeholder",
    ellipsis: true
  }, placeHolderProps), placeHolder));
});

export default Select;
export { flattenSelectObject };
//# sourceMappingURL=select.esm.js.map
